import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { Lotus } from "@/components/Motifs";

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function AdminLogin() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("ipsita_token", data.token);
      navigate("/admin");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main data-testid="admin-login-page" className="min-h-screen bg-ink text-paper flex items-center justify-center px-5 grain">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="w-full max-w-md"
      >
        <div className="flex items-center gap-3 mb-10 justify-center">
          <Lotus className="w-7 h-7 text-alta" />
          <span className="font-serif text-3xl tracking-[0.28em]">IPSITA</span>
        </div>
        <p className="text-center text-[10px] tracking-[0.3em] uppercase text-paper/50 mb-8">Studio CMS</p>

        <form onSubmit={submit} className="space-y-7" data-testid="admin-login-form">
          <div>
            <label htmlFor="admin-email" className="text-[10px] tracking-[0.25em] uppercase text-paper/50">Email</label>
            <input
              id="admin-email"
              data-testid="admin-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-transparent border-b border-paper/25 focus:border-alta outline-none py-3 text-base font-light text-paper transition-colors"
              autoComplete="email"
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="text-[10px] tracking-[0.25em] uppercase text-paper/50">Password</label>
            <input
              id="admin-password"
              data-testid="admin-password-input"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-transparent border-b border-paper/25 focus:border-alta outline-none py-3 text-base font-light text-paper transition-colors"
              autoComplete="current-password"
            />
          </div>
          {error && (
            <p data-testid="admin-login-error" className="text-sm text-alta font-light">{error}</p>
          )}
          <button
            type="submit"
            data-testid="admin-login-submit-button"
            disabled={loading}
            className="w-full bg-alta text-paper py-4 text-[11px] font-medium tracking-[0.25em] uppercase transition-colors duration-500 hover:bg-paper hover:text-ink disabled:opacity-60"
          >
            {loading ? "Entering…" : "Enter the Studio"}
          </button>
        </form>
      </motion.div>
    </main>
  );
}
